// dialog.service.ts
import { Injectable, signal } from '@angular/core';

/**
 * Servicio global para controlar un diálogo de confirmación
 * desde cualquier parte de la aplicación.
 *
 * - Mantiene todo el estado del diálogo en una signal reactiva.
 * - Permite abrir el diálogo con un título y mensaje personalizados.
 * - Retorna una Promise<boolean> que se resuelve cuando el usuario acepta o cancela.
 * - No requiere ViewChild ni passing de @Input: el estado es global.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  /**
   * State signal que contiene todos los datos para mostrar el diálogo.
   *
   * `visible`   → true si el diálogo debe mostrarse.
   * `title`     → título que se mostrará.
   * `message`   → cuerpo del mensaje.
   * `resolve`   → referencia a la función que resuelve la Promise
   *               creada en el método confirm().
   */
  state = signal<{
    visible: boolean;
    title: string;
    message: string;
    resolve?: (result: boolean) => void;
  }>({
    visible: false,
    title: 'Confirmar',
    message: '¿Desea continuar?',
    resolve: undefined,
  });

  /**
   * Abre el diálogo de confirmación y devuelve una Promise<boolean>.
   * La promesa se resuelve cuando el usuario confirma o cancela.
   *
   * @param title   Título del diálogo.
   * @param message Mensaje que se mostrará en el cuerpo del diálogo.
   * @returns Promise<boolean> que se resuelve con true (confirmar) o false (cancelar).
   *
   * Ejemplo:
   * const ok = await dialogService.confirm('Título', 'Mensaje');
   * if (ok) { ... } else { ... }
   */
  confirm(title: string, message: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // Establecemos el estado visible y los datos.
      this.state.set({
        visible: true,
        title,
        message,
        resolve
      });
    });
  }

  /**
   * Método interno para cerrar el diálogo y resolver la promesa pendiente.
   *
   * @param result Valor booleano que indica si el usuario confirmó (true) o canceló (false).
   */
  private close(result: boolean) {
    const current = this.state();

    // Resolvemos la promesa con el resultado.
    if (current.resolve) {
      current.resolve(result);
    }

    // Ocultamos el diálogo y limpiamos la función resolve.
    this.state.set({
      ...current,
      visible: false,
      resolve: undefined
    });
  }

  /**
   * Acción cuando el usuario confirma.
   * Llama internamente a close(true).
   */
  confirmAction() {
    this.close(true);
  }

  /**
   * Acción cuando el usuario cancela.
   * Llama internamente a close(false).
   */
  cancelAction() {
    this.close(false);
  }
}
